import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AlertTriangle, Archive, ReceiptText } from 'lucide-react'
import { useArchiveInvoice, useCorrectInvoiceSerial, useUpdateInvoice } from '@/features/invoices/mutations'
import { useInvoice, useInvoicePoComparison, useInvoiceProgress } from '@/features/invoices/queries'
import { usePurchaseOrderDetails } from '@/api/hooks'
import { BackButton } from '@/components/back-button'
import { InvoiceEquipmentSerialReview } from '@/components/invoice-equipment-serial-review'
import { ActionPopover } from '@/components/action-popover'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { compactDate, firstPoLink, paymentSummary, projectForPoLink } from './formatting'
import { ActionItemCard, type InvoiceCardOperations } from './actions'
import { DuplicateDecisionPopover } from './duplicate-decision'
import { ActivityTimeline } from './activity'
import { NewInvoiceThreadDialog } from './communications'
import { POConversations } from '@/features/communications/po-conversations'
import { useActionFocus, useMessageFocus } from '@/hooks/use-action-focus'
import { ActionEmailContext } from '@/features/communications/action-email'
import { ACCOUNTING_SIGNOFF } from '@/features/communications/formatting'
import {
  useApInvoiceCommunications,
  useApproveMessage,
  useRejectMessage,
  useSendMessage,
  useUpdateMessage,
} from '@/features/communications/hooks'
import { FreightChargeReview } from './freight'
import { useInvoicePayment } from './use-payment'
import { PaymentHistory, PaymentDialogs } from './payments'
import { useInvoiceDocuments } from './use-documents'
import { InvoicePoComparison, InvoiceDocuments } from './documents'
import { useInvoiceApproval } from './use-approval'
import { ApprovalPath, InvoiceApprovalControls, InvoiceApprovalNotices } from './approval'
import { useInvoiceSummary } from './use-summary'
import {
  InvoiceDetailFields,
  InvoicePaymentDetails,
  InvoiceStateControls,
  InvoiceSummaryDialogs,
  LinkPoPopover,
} from './summary'
import { InvoicePoMatch } from './po-match'
import { InvoiceAutomation } from './automation'
import { VendorCreditLine } from './credits'
import { InvoiceProgress } from './progress'
import { InvoiceNotes } from './notes'
import { invoiceNotesContext } from './notes-context'
import { DetailSection, DetailSections } from './section'
import { usePageTitle } from '@/lib/page-title'
import { WaitingOn } from '@/features/owed-items/waiting-on'
import { getApiErrorMessage } from '@/lib/api-error'

type InvoiceDetailPageProps = {
  invoiceId: string
  /** An email to open and highlight, by message id (an action item's link to its source email). */
  messageId?: number
  /** An action item to bring into view, opened from the Action Items page (USA-1184). */
  actionUuid?: string
}

export function InvoiceDetailPage({ invoiceId, messageId, actionUuid }: InvoiceDetailPageProps) {
  const { data: invoice, isLoading } = useInvoice(invoiceId)
  usePageTitle(
    invoice &&
      (invoice.invoice_number
        ? `AP ${invoice.invoice_number}`
        : invoice.vendor?.name || invoice.vendor_raw),
  )
  const poComparison = useInvoicePoComparison(invoiceId)
  const progress = useInvoiceProgress(invoiceId)
  const updateInvoice = useUpdateInvoice(invoiceId)
  const correctSerial = useCorrectInvoiceSerial(invoiceId)
  const approval = useInvoiceApproval(invoiceId, invoice, updateInvoice)
  const { approvalCapabilities } = approval
  const archiveInvoice = useArchiveInvoice(invoiceId)
  const { getAccessToken } = useAuth()
  // Serial numbers map to PO lines, so the linked POs' lines load only for an invoice that lists serials.
  const hasSerials = (invoice?.lines ?? []).some((line) => line.serials.length > 0)
  const linkedPurchaseOrderUuids = hasSerials
    ? (invoice?.po_links ?? [])
        .map((link) => link.purchase_order?.uuid)
        .filter((uuid): uuid is string => Boolean(uuid))
    : []
  const linkedPurchaseOrderQueries = usePurchaseOrderDetails(linkedPurchaseOrderUuids)

  const openActions = useMemo(() => {
    return (invoice?.actions ?? [])
      .filter((action) => action.status === 'open')
      .sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''))
  }, [invoice?.actions])
  const resolvedActions = useMemo(
    () => (invoice?.actions ?? []).filter((action) => action.status !== 'open'),
    [invoice?.actions],
  )
  const invoiceMessagesById = useMemo(() => {
    const messages = (invoice?.communication_threads ?? []).flatMap(
      (thread) => thread.messages ?? [],
    )
    return new Map(messages.map((message) => [message.id, message]))
  }, [invoice?.communication_threads])

  const payment = useInvoicePayment(invoiceId, invoice, getAccessToken)
  const { openPaymentDialog } = payment
  const documents = useInvoiceDocuments(invoiceId, invoice, getAccessToken)
  const summary = useInvoiceSummary(invoiceId, invoice, updateInvoice)
  const invoiceThreads = useApInvoiceCommunications(invoiceId)
  useMessageFocus(messageId, !!invoice && !invoiceThreads.isLoading)
  // The page renders once this user's approval buttons load too; the item isn't there to scroll to before (USA-1641).
  useActionFocus(actionUuid, !!invoice && !approvalCapabilities.isLoading)
  const approveMessage = useApproveMessage()
  const rejectMessage = useRejectMessage()
  const sendMessage = useSendMessage()
  const updateMessage = useUpdateMessage()

  // The invoice operations each card offers (USA-1418): the PM approves, Accounting pays.
  const allowedDecisions = approvalCapabilities.data?.allowed_decisions ?? []
  const enforced = approvalCapabilities.data?.enforcement_enabled === true
  // Approval waits for this user's capabilities, like the page's own approval buttons.
  const capabilitiesLoaded = !!approvalCapabilities.data
  // The PM approves an invoice over its PO by more than $5 before Accounting (USA-1418, USA-1702).
  const pmReviewOpen = openActions.some((action) => action.action_type === 'operations_approval_needed')
  // Only Finance (or an admin) approves an invoice of $10,000 or more waiting for Finance (USA-1472).
  const financeApprover = ['finance_approver', 'ap_admin'].includes(approvalCapabilities.data?.actor_role ?? '')
  const approveForPayment = () =>
    updateInvoice.mutate(
      { status: 'payment_ready' },
      {
        onSuccess: () => toast.success('Invoice approved'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Approval failed')),
      },
    )
  // The PM's approval hands the invoice to Accounting (USA-1702).
  const approveForAccounting = () =>
    updateInvoice.mutate(
      { status: 'needs_approval' },
      {
        onSuccess: () => toast.success('Invoice approved; Accounting reviews it next'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Approval failed')),
      },
    )
  const cardOperations: InvoiceCardOperations = {
    approve: !capabilitiesLoaded
      ? undefined
      : enforced
      ? allowedDecisions.includes('approve_operations')
        ? () => approval.runApprovalDecision('approve_operations', 'Invoice approved')
        : allowedDecisions.includes('approve_accounting')
          ? approval.approveAccounting
          : undefined
      : invoice?.status === 'needs_approval'
        ? approval.quickApprove
        : invoice?.status === 'operations_review' && pmReviewOpen
          ? approveForAccounting
          : (invoice?.status === 'approved_by_pc' && pmReviewOpen) ||
            (invoice?.status === 'finance_review' && financeApprover)
          ? approveForPayment
          : undefined,
    approvePending: approval.decideApproval.isPending || updateInvoice.isPending,
    requestAdjustment:
      capabilitiesLoaded && (!enforced || allowedDecisions.includes('request_adjustment'))
        ? (reason) =>
            approval.decideApproval.mutate(
              { decision: 'request_adjustment', reason, expected_updated_at: invoice!.updated_at },
              {
                onSuccess: () => toast.success('Adjustment requested'),
                onError: (err) =>
                  toast.error(getApiErrorMessage(err, 'Adjustment request failed')),
              },
            )
        : undefined,
    requestAdjustmentPending: approval.decideApproval.isPending,
    recordPayment: openPaymentDialog,
    paymentInvoice: invoice?.uuid
      ? {
          uuid: invoice.uuid,
          label: invoice.invoice_number ? `Invoice ${invoice.invoice_number}` : 'Invoice',
          paid: invoice.status === 'paid',
          balance: String(invoice.remaining_balance ?? invoice.invoice_total ?? '') || null,
          pays_in_advance: ['cash_in_advance', 'prepaid_at_order'].includes(invoice.payment_policy ?? ''),
          payment_method: 'ach',
          record_payment: invoice.status !== 'paid',
        }
      : undefined,
    duplicateDecision: (trigger) => (
      <DuplicateDecisionPopover invoiceId={invoiceId} trigger={trigger} align="start" />
    ),
    acceptTotal: (amount, onDone) =>
      updateInvoice.mutate(
        { invoice_total: amount },
        {
          onSuccess: () => {
            toast.success('Invoice total updated')
            onDone()
          },
          onError: (err) => toast.error(getApiErrorMessage(err, 'Update failed')),
        },
      ),
  }

  // Wait for this user's approval buttons too, so they don't push the page down a moment later (USA-1536).
  if (isLoading || approvalCapabilities.isLoading) {
    return <div className="py-12 text-center text-muted-foreground">Loading invoice...</div>
  }

  if (!invoice) {
    return <div className="py-12 text-center text-muted-foreground">Invoice not found</div>
  }

  const vendorName = invoice.vendor?.name || invoice.vendor_raw || 'Unlinked AP entity'
  const manufacturer = invoice.manufacturer || invoice.vendor?.manufacturer
  const firstPo = firstPoLink(invoice)
  const project = invoice.internal_project || projectForPoLink(firstPo)
  const linkedPurchaseOrderLines = linkedPurchaseOrderQueries.flatMap((query) =>
    (query.data?.line_items ?? []).map((line) => ({
      ...line,
      po_number: query.data?.po_number ?? '',
    })),
  )

  const canRecordPaymentEvidence = approvalCapabilities.data?.payment_evidence_allowed === true
  const overdue =
    (invoice.official_due_date || invoice.priority_due_date) &&
    new Date(invoice.official_due_date || invoice.priority_due_date || '') < new Date()

  const submitArchive = (archiveReason: string, close: () => void) => {
    if (!archiveReason) {
      toast.error('Archive reason is required')
      return
    }
    archiveInvoice.mutate(
      { reason: archiveReason },
      {
        onSuccess: () => {
          toast.success('Invoice archived')
          close()
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Archive failed')),
      },
    )
  }

  const notesContext = invoiceNotesContext(invoice)
  const carrierInvoice = !!invoice.wwex_carrier_invoices?.length && !invoice.po_links?.length
  const invoiceFileCount = documents.invoiceDocuments.length
  const historyCount = resolvedActions.length + (invoice.audit_events?.length ?? 0)

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 pb-24">
      <BackButton fallback={{ to: '/invoices' }} fallbackLabel="AP invoices" />

      <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">
            {invoice.vendor?.id != null ? (
              <Link
                to="/ap-vendors/$vendorId"
                params={{ vendorId: String(invoice.vendor.id) }}
                className="hover:underline"
              >
                {vendorName}
              </Link>
            ) : (
              vendorName
            )}
            {invoice.invoice_number ? ` · ${invoice.invoice_number}` : ''}
          </h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
            <InvoiceStateControls
              invoice={invoice}
              summary={summary}
              approvalCapabilities={approvalCapabilities}
              updateInvoice={updateInvoice}
            />
            {invoice.status !== 'paid' && (
              <span className={overdue ? 'font-medium text-red-700' : undefined}>
                {overdue ? 'Overdue · due ' : 'Due '}
                {compactDate(invoice.official_due_date || invoice.priority_due_date)}
              </span>
            )}
            {project?.uuid && (
              <Link
                to="/projects/$projectId"
                params={{ projectId: project.uuid }}
                className="text-primary hover:underline"
              >
                {project.name}
              </Link>
            )}
            {invoice.duplicate_status === 'possible_duplicate' && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" /> Possible duplicate
              </Badge>
            )}
            {invoice.archived_at && <Badge variant="outline">Archived</Badge>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {['possible_duplicate', 'confirmed_duplicate'].includes(
            invoice.duplicate_status || '',
          ) && (
            <DuplicateDecisionPopover
              invoiceId={invoiceId}
              trigger={
                <Button size="sm" variant="outline">
                  <AlertTriangle className="h-3.5 w-3.5" /> Resolve duplicate
                </Button>
              }
            />
          )}
          <InvoiceApprovalControls
            invoice={invoice}
            approval={approval}
            updateInvoice={updateInvoice}
          />
          {invoice.status !== 'paid' && (
            <Button
              size="sm"
              variant="outline"
              onClick={openPaymentDialog}
              disabled={!approvalCapabilities.data || !canRecordPaymentEvidence}
            >
              <ReceiptText className="h-3.5 w-3.5" /> Record payment
            </Button>
          )}
          <ActionPopover
            align="end"
            trigger={
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Archive"
                title="Archive"
                disabled={!!invoice.archived_at || archiveInvoice.isPending}
              >
                <Archive className="h-4 w-4" />
              </Button>
            }
            title="Archive invoice"
            note={{
              label: 'Archive reason',
              placeholder: 'Reason for archive/dismissal',
              minLength: 1,
            }}
            confirmLabel="Archive invoice"
            confirmVariant="destructive"
            pending={archiveInvoice.isPending}
            pendingLabel="Archiving..."
            onConfirm={submitArchive}
          />
        </div>
      </header>

      <InvoiceProgress progress={progress.data} />

      <InvoiceApprovalNotices approvalCapabilities={approvalCapabilities} />
      {approvalCapabilities.data?.ownership_error && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          {approvalCapabilities.data.ownership_error}
        </p>
      )}

      {carrierInvoice ? (
        <FreightChargeReview
          carrierInvoices={invoice.wwex_carrier_invoices ?? []}
          allocations={invoice.shipment_allocations ?? []}
          variances={invoice.variances ?? []}
        />
      ) : (
        <InvoicePoMatch
          comparison={poComparison.data}
          isLoading={poComparison.isLoading}
          action={
            <LinkPoPopover summary={summary} />
          }
        />
      )}

      {/* What the invoice says, then the documents to check it against (USA-1178). */}
      <InvoicePoComparison invoice={invoice} documents={documents} />

      {/* Right under the invoice: what should change on it, for Accounting (USA-1180). */}
      {notesContext && <InvoiceNotes context={notesContext} />}

      {openActions.length > 0 && (
        <section className="space-y-3" aria-labelledby="invoice-actions-heading">
          <h2 id="invoice-actions-heading" className="text-sm font-semibold">
            Needs attention
          </h2>
          {openActions.map((action) => (
            <ActionItemCard
              key={action.id}
              action={action}
              invoiceId={invoiceId}
              notes={notesContext}
              actorRole={approvalCapabilities.data?.actor_role}
              operations={cardOperations}
              draftMessage={
                action.communication_message_id
                  ? invoiceMessagesById.get(action.communication_message_id)
                  : undefined
              }
            />
          ))}
        </section>
      )}
      <WaitingOn scope={{ kind: 'ap_invoice', invoiceUuid: invoiceId }} />

      {hasSerials && (
        <InvoiceEquipmentSerialReview
          lines={invoice.lines ?? []}
          purchaseOrderLines={linkedPurchaseOrderLines}
          purchaseOrderLinesLoading={linkedPurchaseOrderQueries.some((query) => query.isPending)}
          purchaseOrderLinesError={linkedPurchaseOrderQueries.some((query) => query.isError)}
          purchaseOrderNumber={firstPo?.purchase_order?.po_number || firstPo?.po_number_raw}
          correction={correctSerial}
          canCorrect={['accounting', 'finance_approver', 'project_coordinator', 'ap_admin'].includes(
            approvalCapabilities.data?.actor_role ?? '',
          )}
        />
      )}

      <DetailSections>
        <DetailSection title="Payment" summary={paymentSummary(invoice)}>
          <div className="space-y-4">
            <InvoicePaymentDetails invoice={invoice} summary={summary} />
            <VendorCreditLine
              invoice={invoice}
              invoiceId={invoiceId}
              canApply={canRecordPaymentEvidence}
            />
            <PaymentHistory invoice={invoice} payment={payment} />
          </div>
        </DetailSection>
        <DetailSection title="Invoice details" summary={invoice.notes_lesley || invoice.notes_pc}>
          <InvoiceDetailFields
            invoice={invoice}
            invoiceId={invoiceId}
            summary={summary}
            updateInvoice={updateInvoice}
            vendorName={vendorName}
            manufacturer={manufacturer}
          />
        </DetailSection>
        <DetailSection title="Automation" summary={invoice.automation_disabled ? 'Off' : 'On'}>
          <InvoiceAutomation
            invoiceId={invoiceId}
            automationDisabled={invoice.automation_disabled ?? false}
            updateInvoice={updateInvoice}
          />
        </DetailSection>
        {!carrierInvoice && !!invoice.wwex_carrier_invoices?.length && (
          <DetailSection title="Shipment charges">
            <FreightChargeReview
              carrierInvoices={invoice.wwex_carrier_invoices}
              allocations={invoice.shipment_allocations ?? []}
              variances={invoice.variances ?? []}
            />
          </DetailSection>
        )}
        <DetailSection
          title="Files"
          summary={`${invoiceFileCount} ${invoiceFileCount === 1 ? 'file' : 'files'}`}
        >
          <InvoiceDocuments invoice={invoice} documents={documents} />
        </DetailSection>
        <DetailSection title="History" summary={historyCount ? undefined : 'Nothing yet'}>
          <div className="space-y-4">
            {approvalCapabilities.data?.enforcement_enabled && (
              <ApprovalPath
                status={invoice.status}
                approvals={invoice.approvals ?? []}
                operationsOwner={approvalCapabilities.data.operations_owner}
                financeRequired={approvalCapabilities.data.finance_reasons.length > 0}
              />
            )}
            {resolvedActions.length > 0 && (
              <div className="space-y-2">
                {resolvedActions.map((action) => (
                  <ActionItemCard
                    key={action.id}
                    action={action}
                    invoiceId={invoiceId}
                    notes={notesContext}
                    actorRole={approvalCapabilities.data?.actor_role}
                    draftMessage={
                      action.communication_message_id
                        ? invoiceMessagesById.get(action.communication_message_id)
                        : undefined
                    }
                    history
                  />
                ))}
              </div>
            )}
            <ActivityTimeline events={invoice.audit_events} invoice={invoice} />
          </div>
        </DetailSection>
      </DetailSections>

      {/* The same conversation view as a PO: threads, messages, replies, and each item under its email. */}
      <ActionEmailContext.Provider
        value={{
          focusMessageId: messageId,
          renderAction: (item) => {
            const action = openActions.find((candidate) => candidate.id === item.id)
            return action ? (
              <ActionItemCard
                action={action}
                invoiceId={invoiceId}
                notes={notesContext}
                actorRole={approvalCapabilities.data?.actor_role}
                operations={cardOperations}
                underSourceEmail
              />
            ) : null
          },
        }}
      >
        <POConversations
          threads={invoiceThreads.data}
          isLoading={invoiceThreads.isLoading}
          actions={(invoice.actions ?? []).map((action) => ({
            ...action,
            created_at: action.created_at ?? action.updated_at ?? '',
            communication_message_id: action.card?.source_email?.message_id ?? null,
          }))}
          hasPoPdf={false}
          hasQuotePdf={false}
          shipmentDocOptions={[]}
          contextForThread={() => ({
            signoff: ACCOUNTING_SIGNOFF,
            poDocuments: false,
            canRecord: false,
          })}
          onApprove={(uuid) => approveMessage.mutate(uuid)}
          onReject={(uuid) => rejectMessage.mutate(uuid)}
          onSend={(uuid) => sendMessage.mutate(uuid)}
          onUpdate={(uuid, body) => updateMessage.mutate({ messageUuid: uuid, body })}
          newThreadAction={<NewInvoiceThreadDialog invoice={invoice} />}
          emptyState={
            <div className="flex flex-col items-center gap-3 py-8">
              <p className="text-sm text-muted-foreground">No invoice emails yet</p>
              <NewInvoiceThreadDialog invoice={invoice} />
            </div>
          }
        />
      </ActionEmailContext.Provider>

      <InvoiceSummaryDialogs summary={summary} updateInvoice={updateInvoice} />

      <PaymentDialogs payment={payment} />
    </div>
  )
}
